import { useState, useEffect, useCallback } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight, Building2, Moon, Sofa, BedDouble, ChefHat, Droplets, Shirt, DoorOpen, Baby } from 'lucide-react';
import { Picture } from './Picture';

/** "salon", 7 → ["gallery/salon-01", … "gallery/salon-07"] */
const keys = (slug: string, count: number) =>
  Array.from({ length: count }, (_, i) => `gallery/${slug}-${String(i + 1).padStart(2, '0')}`);

const CATEGORIES = [
  { name: 'Dış Cephe - Gündüz', icon: Building2, color: 'from-amber-500 to-orange-500',   images: keys('dis-cephe-gunduz', 4) },
  { name: 'Dış Cephe - Gece',   icon: Moon,      color: 'from-slate-600 to-slate-800',    images: keys('dis-cephe-gece', 4) },
  { name: 'Salon',              icon: Sofa,      color: 'from-blue-500 to-indigo-500',    images: keys('salon', 7) },
  { name: 'Yatak Odası',        icon: BedDouble, color: 'from-violet-500 to-purple-600',  images: keys('yatak-odasi', 5) },
  { name: 'Mutfak',             icon: ChefHat,   color: 'from-rose-500 to-pink-600',      images: keys('mutfak', 5) },
  { name: 'Banyo',              icon: Droplets,  color: 'from-cyan-500 to-teal-500',      images: keys('banyo', 9) },
  { name: 'Giyinme Odası',      icon: Shirt,     color: 'from-fuchsia-500 to-pink-500',   images: keys('giyinme-odasi', 3) },
  { name: 'Antre / Hol',        icon: DoorOpen,  color: 'from-yellow-500 to-amber-500',   images: keys('antre', 4) },
  { name: 'Çocuk Odası',        icon: Baby,      color: 'from-green-500 to-emerald-500',  images: keys('cocuk-odasi', 4) },
];

type LightboxState = { categoryIndex: number; photoIndex: number } | null;

export function Gallery() {
  const [lightbox, setLightbox] = useState<LightboxState>(null);

  const close = useCallback(() => setLightbox(null), []);

  const prev = useCallback(() => {
    setLightbox((lb) => {
      if (!lb) return null;
      const cat = CATEGORIES[lb.categoryIndex];
      return { ...lb, photoIndex: (lb.photoIndex - 1 + cat.images.length) % cat.images.length };
    });
  }, []);

  const next = useCallback(() => {
    setLightbox((lb) => {
      if (!lb) return null;
      const cat = CATEGORIES[lb.categoryIndex];
      return { ...lb, photoIndex: (lb.photoIndex + 1) % cat.images.length };
    });
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, close, prev, next]);

  const activeCat = lightbox !== null ? CATEGORIES[lightbox.categoryIndex] : null;
  const activeImg = lightbox !== null && activeCat ? activeCat.images[lightbox.photoIndex] : '';

  return (
    <section id="gallery" className="py-24 bg-white dark:bg-slate-950">
      <div className="container mx-auto px-4 lg:px-8">
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full text-sm tracking-wider mb-4">
            GALERİ
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-6">
            Projeyi{' '}
            <span className="text-stone-700 dark:text-stone-300">Yakından Keşfedin</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Kategoriye tıklayarak Rain Nova'nın her köşesini keşfedin
          </p>
        </m.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {CATEGORIES.map((cat, catIdx) => (
            <m.button
              key={cat.name}
              type="button"
              onClick={() => setLightbox({ categoryIndex: catIdx, photoIndex: 0 })}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: catIdx * 0.05 }}
              className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 aspect-[4/3] cursor-pointer"
            >
              {/* Kapak — ızgarada en fazla ~420px genişlik, 600px türev yeterli */}
              <Picture
                media={cat.images[0]}
                alt={`${cat.name} — ${cat.images.length} fotoğraf`}
                sizes="(min-width: 768px) 33vw, 50vw"
                width={600}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 px-4 pb-5 text-center">
                <div className="text-white font-semibold text-base tracking-wide drop-shadow-lg">{cat.name}</div>
              </div>
            </m.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && activeCat && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-label={`${activeCat.name} galerisi`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${activeCat.color} flex items-center justify-center`}>
                  <activeCat.icon size={15} className="text-white" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-white font-semibold">{activeCat.name}</div>
                  <div className="text-white/50 text-xs">{lightbox.photoIndex + 1} / {activeCat.images.length}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className="text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Galeriyi kapat"
              >
                <X size={28} aria-hidden="true" />
              </button>
            </div>

            {/* Görsel alanı — tam boyut yalnızca burada iner */}
            {/* Dar ekranda px-16 görselin genişliğinin üçte birini yiyordu; oklar
                mobilde görselin üzerine biniyor, bu yüzden kendi zemini var. */}
            <div className="flex-1 flex items-center justify-center relative min-h-0 px-2 sm:px-16" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={prev}
                className="absolute left-2 sm:left-4 z-10 text-white/80 hover:text-white p-2 sm:p-3 rounded-full bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
                aria-label="Önceki fotoğraf"
              >
                <ChevronLeft size={28} className="sm:w-9 sm:h-9" aria-hidden="true" />
              </button>

              <AnimatePresence mode="wait">
                <m.div
                  key={activeImg}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.2 }}
                  className="max-h-full max-w-full flex items-center justify-center"
                >
                  <Picture
                    media={activeImg}
                    alt={`${activeCat.name} — fotoğraf ${lightbox.photoIndex + 1}`}
                    sizes="(min-width: 640px) calc(100vw - 8rem), calc(100vw - 1rem)"
                    width={2000}
                    loading="eager"
                    className="max-h-full max-w-full w-auto h-auto object-contain rounded-lg select-none"
                  />
                </m.div>
              </AnimatePresence>

              <button
                type="button"
                onClick={next}
                className="absolute right-2 sm:right-4 z-10 text-white/80 hover:text-white p-2 sm:p-3 rounded-full bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
                aria-label="Sonraki fotoğraf"
              >
                <ChevronRight size={28} className="sm:w-9 sm:h-9" aria-hidden="true" />
              </button>
            </div>

            {/* Küçük resim şeridi — en küçük türev seçilir */}
            {/* w-max + mx-auto: sığdığında ortalanır, sığmadığında baştan sona
                kaydırılabilir. justify-center tek başına şeridin başını kırpıyordu. */}
            <div className="flex-shrink-0 py-4 px-6 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex gap-2 w-max mx-auto">
                {activeCat.images.map((img, i) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setLightbox({ categoryIndex: lightbox.categoryIndex, photoIndex: i })}
                    aria-label={`${i + 1}. fotoğrafa geç`}
                    aria-current={i === lightbox.photoIndex}
                    className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      i === lightbox.photoIndex
                        ? 'border-white scale-110 shadow-lg'
                        : 'border-transparent opacity-50 hover:opacity-80'
                    }`}
                  >
                    <Picture
                      media={img}
                      alt=""
                      sizes="56px"
                      width={56}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </section>
  );
}
